import { useEffect, useMemo, useState } from "react"
import { History, Search } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { assistant_chats_path } from "@/routes"
import { cn } from "@/lib/utils"

interface ChatSummary {
  uid: string
  title: string
  created_at: string
  updated_at: string
}

type Bucket = "today" | "yesterday" | "last_7" | "last_30" | "older"

const BUCKET_ORDER: Bucket[] = ["today", "yesterday", "last_7", "last_30", "older"]
const BUCKET_LABELS: Record<Bucket, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last_7: "Last 7 days",
  last_30: "Last 30 days",
  older: "Older",
}

function bucketFor(createdAt: Date, now: Date): Bucket {
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const day = 24 * 60 * 60 * 1000
  if (createdAt >= todayStart) return "today"
  if (createdAt >= new Date(todayStart.getTime() - day)) return "yesterday"
  if (createdAt >= new Date(todayStart.getTime() - 7 * day)) return "last_7"
  if (createdAt >= new Date(todayStart.getTime() - 30 * day)) return "last_30"
  return "older"
}

interface AssistantHistoryProps {
  accountUid: string
  currentChatUid: string | null
  onSelectChat: (uid: string) => void
}

export function AssistantHistory({
  accountUid,
  currentChatUid,
  onSelectChat,
}: AssistantHistoryProps) {
  const [open, setOpen] = useState(false)
  const [chats, setChats] = useState<ChatSummary[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    fetch(assistant_chats_path(accountUid), {
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load history")
        return res.json()
      })
      .then((data: { chats: ChatSummary[] }) => {
        if (cancelled) return
        setChats(data.chats)
      })
      .catch(() => {
        if (cancelled) return
        toast.error("Failed to load chat history")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, accountUid])

  const filtered = useMemo(() => {
    if (!chats) return []
    const q = query.trim().toLowerCase()
    if (!q) return chats
    return chats.filter((c) => c.title.toLowerCase().includes(q))
  }, [chats, query])

  const grouped = useMemo(() => {
    const groups: Record<Bucket, ChatSummary[]> = {
      today: [],
      yesterday: [],
      last_7: [],
      last_30: [],
      older: [],
    }
    const now = new Date()
    for (const chat of filtered) {
      const bucket = bucketFor(new Date(chat.created_at), now)
      groups[bucket].push(chat)
    }
    return groups
  }, [filtered])

  const isEmpty = !loading && chats !== null && filtered.length === 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="icon-sm"
          variant="ghost"
          type="button"
          aria-label="Chat history"
        >
          <History className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats"
              autoFocus
              className="pl-7 text-sm"
            />
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {loading && (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              Loading…
            </p>
          )}
          {isEmpty && (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              {query.trim() ? "No matching chats" : "No past chats yet"}
            </p>
          )}
          {!loading &&
            BUCKET_ORDER.map((bucket) => {
              const list = grouped[bucket]
              if (list.length === 0) return null
              return (
                <div key={bucket} className="py-1">
                  <div className="px-3 py-1 text-xs font-medium text-muted-foreground">
                    {BUCKET_LABELS[bucket]}
                  </div>
                  <ul>
                    {list.map((chat) => {
                      const isActive = chat.uid === currentChatUid
                      return (
                        <li key={chat.uid}>
                          <button
                            type="button"
                            onClick={() => {
                              onSelectChat(chat.uid)
                              setOpen(false)
                            }}
                            className={cn(
                              "block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-muted",
                              isActive && "bg-muted font-medium",
                            )}
                            title={chat.title}
                          >
                            {chat.title}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
